// Uma sessão por backend/canal/operador; nunca persiste credenciais ou HTML.
export const submissionKey = scope => `superpizza:order:v1:${JSON.stringify(scope)}`;
const pending = record => ["enviando", "incerto"].includes(record.estado);
const clone = value => JSON.parse(JSON.stringify(value));
const uuid = value => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
const validItem = item => {
  if (!item || !/^(\d+|half-\d+-\d+)$/.test(String(item.id))) return false;
  const ids = String(item.id).replace(/^half-/, "").split("-").map(Number);
  return ids.every(id => Number.isSafeInteger(id) && id > 0) && new Set(ids).size === ids.length &&
    Number.isInteger(item.qty) && item.qty > 0 && item.qty <= 999;
};

export function createOrderSubmission(storage, scope, legacyKey) {
  const key = submissionKey(scope);
  let record = { schema_version: 1, scope, estado: "rascunho", rascunho: { cart: [] } };
  let error = "";
  let busy = false;
  try {
    if (scope.canal === "painel" && (!scope.usuario || scope.usuario === "sem-operador")) throw new Error("missing operator");
    const raw = storage.getItem(key);
    if (raw) {
      const saved = JSON.parse(raw);
      if (saved.schema_version !== 1 || JSON.stringify(saved.scope) !== JSON.stringify(scope) ||
          !["rascunho", "enviando", "incerto", "aceito", "recusado"].includes(saved.estado) ||
          !Array.isArray(saved.rascunho?.cart) || !saved.rascunho.cart.every(validItem) ||
          (saved.rascunho.orderForm && typeof saved.rascunho.orderForm !== "object") ||
          (saved.rascunho.step && ![1, 2, 3, 4].includes(saved.rascunho.step)) ||
          (pending(saved) && (!uuid(saved.tentativa?.id_externo) || saved.tentativa.id_externo !== saved.tentativa.corpo?.id_externo)) ||
          (saved.estado === "aceito" && !saved.resultado?.pedido_uid)) {
        throw new Error("invalid snapshot");
      }
      record = saved.estado === "enviando" ? { ...saved, estado: "incerto" } : saved;
    } else if (legacyKey) {
      const cart = JSON.parse(storage.getItem(legacyKey) || "[]");
      if (!Array.isArray(cart) || !cart.every(validItem)) throw new Error("invalid cart");
      record.rascunho.cart = cart.map(({ id, qty, nome_item, tipo, tamanho, valor }) =>
        ({ id, qty, nome_item, tipo, tamanho, valor }));
    }
  } catch {
    error = "Não foi possível recuperar o pedido nesta sessão. Não envie outra compra antes de conferir com a loja.";
  }
  function write(next) {
    if (error) throw new Error(error);
    try {
      const saved = clone({ ...next, atualizado_em: new Date().toISOString() });
      storage.setItem(key, JSON.stringify(saved));
      record = saved;
    } catch {
      throw new Error("Não foi possível salvar o pedido nesta sessão. Libere o armazenamento antes de enviar ou retomar.");
    }
  }
  return {
    get record() { return record; },
    get error() { return error; },
    get locked() { return !!error || pending(record) || record.estado === "aceito"; },
    saveDraft(rascunho) {
      if (!this.locked) write({ ...record, rascunho });
    },
    newOrder() {
      if (error || pending(record) || busy) throw new Error("Confira a tentativa pendente antes de iniciar outra compra.");
      write({ schema_version: 1, scope, estado: "rascunho", rascunho: { cart: [] } });
    },
    async submit(body, send) {
      if (busy) return null;
      if (error) throw new Error(error);
      if (record.estado === "aceito") throw new Error("Este pedido já foi recebido. Inicie uma nova compra.");
      busy = true;
      const recovering = pending(record);
      let started = false;
      try {
        const corpo = recovering ? record.tentativa.corpo : clone({ ...body, id_externo: crypto.randomUUID() });
        delete corpo.html;
        delete corpo.items_html;
        delete corpo.html_comanda;
        write({ ...record, estado: "enviando", tentativa: { id_externo: corpo.id_externo, corpo } });
        started = true;
        const response = await send(clone(corpo));
        const result = response.data.pedido || response.data;
        if (!result.pedido_uid || !Number.isInteger(result.versao)) throw new Error("Resposta incompleta; retome o envio para conferir.");
        write({ ...record, estado: "aceito", rascunho: { cart: [] }, tentativa: undefined,
          resultado: { id: result.id, pedido_uid: result.pedido_uid, versao: result.versao, repetido: !!response.data.repetido } });
        return { response, recovering };
      } catch (err) {
        if (started) {
          const status = err.response?.status;
          const code = err.response?.headers?.["x-order-error"];
          const rejected = !recovering && (status === 422 || (status === 409 && code === "total_mismatch"));
          // A gravação anterior permanece recuperável mesmo se o storage falhar agora.
          try { write({ ...record, estado: rejected ? "recusado" : "incerto" }); } catch { /* manter tentativa */ }
        }
        throw err;
      } finally { busy = false; }
    },
  };
}
